import React, {useMemo} from 'react'
import dayjs from 'dayjs'
import {Box, Card, CardContent, Skeleton, Stack, Typography} from '@mui/material'
import {alpha, useTheme} from '@mui/material/styles'
import {ResponsiveContainer, CartesianGrid, XAxis, YAxis, BarChart, Bar} from 'recharts'
import {useTranslation} from 'react-i18next'
import {useGetInvoicesQuery, useGetCurrentUserQuery} from 'store/Store'
import useResponsive from 'logic/useResponsive'
import DashboardChartTooltip from 'components/Page/Dashboard/DashboardChartTooltip'
import INVOICE_STATUS from 'constants/enum/invoiceStatus'

const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
]

const InvoicesCostChart = () => {
    const theme = useTheme()
    const {t} = useTranslation()
    const {smallScreen} = useResponsive()
    const {data: user} = useGetCurrentUserQuery()
    const {data: invoicesResponse, isFetching} = useGetInvoicesQuery({pageSize: 1000})

    const initInvoices = useMemo(
        () => invoicesResponse?.data || invoicesResponse || [],
        [invoicesResponse]
    )

    const invoices = useMemo(() => {
        return initInvoices.filter((invoice) => invoice?.status === INVOICE_STATUS.ACTIVE)
    }, [initInvoices])
    const groupKey = user?.account ? 'consumer' : 'account'

    const {barData, groups} = useMemo(() => {
        if (!Array.isArray(invoices) || !invoices.length) {
            return {barData: [], groups: []}
        }

        const currentYear = dayjs().year()
        const monthTotals = Array.from({length: 12}, () => new Map())
        const groupSet = new Set()

        invoices.forEach((invoice) => {
            const dateValue = invoice?.created || invoice?.from_date || invoice?.date
            if (!dateValue) return
            const parsedDate = dayjs(dateValue)
            if (!parsedDate.isValid() || parsedDate.year() !== currentYear) return

            const monthIndex = parsedDate.month()
            const groupName =
                invoice?.[groupKey]?.name || invoice?.[groupKey] || t('general.notAvailable')
            groupSet.add(groupName)

            const totalPrice = Number(invoice?.total_price || invoice?.price || 0)
            if (!Number.isFinite(totalPrice)) return

            const monthMap = monthTotals[monthIndex]
            monthMap.set(groupName, (monthMap.get(groupName) || 0) + totalPrice)
        })

        const groupList = Array.from(groupSet)
        const barRows = MONTH_NAMES.map((label, index) => {
            const row = {month: t(`months.short.${label.toLowerCase()}`, label)}
            let total = 0
            groupList.forEach((group) => {
                const value = Number((monthTotals[index].get(group) || 0).toFixed(2))
                row[group] = value
                total += value
            })
            row.total = Number(total.toFixed(2))
            return row
        })

        return {barData: barRows, groups: groupList}
    }, [invoices, groupKey, t])

    const palette = useMemo(
        () => [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.success.main,
            theme.palette.warning.main,
            theme.palette.info.main,
            theme.palette.error.main,
            '#8b5cf6',
            '#14b8a6',
            '#f97316',
        ],
        [theme.palette]
    )

    const hasData = barData.some((row) => row.total && row.total > 0)

    let barContent
    if (isFetching) {
        barContent = <Skeleton variant="rounded" height={280} sx={{mt: 2}} />
    } else if (!hasData) {
        barContent = (
            <Typography variant="body2" color="text.secondary" sx={{mt: 2}}>
                {t('dashboard.invoices_cost_empty', 'No invoice data available for this view.')}
            </Typography>
        )
    } else {
        barContent = (
            <Box sx={{width: '100%', height: 320}}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData} margin={{top: 20, right: 20, left: 0, bottom: 20}}>
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={alpha(theme.palette.divider, 0.4)}
                        />
                        <XAxis
                            dataKey="month"
                            tick={{fontSize: 12, fill: theme.palette.text.secondary}}
                            tickLine={false}
                        />
                        {!smallScreen && (
                            <YAxis
                                tick={{fontSize: 12, fill: theme.palette.text.secondary}}
                                tickFormatter={(value) => `${value.toFixed(0)}₪`}
                                tickLine={false}
                                axisLine={{stroke: alpha(theme.palette.divider, 0.4)}}
                                tickMargin={50}
                            />
                        )}
                        <DashboardChartTooltip
                            cursor={{fill: alpha(theme.palette.primary.main, 0.04)}}
                            formatter={(value, name) => [
                                `${Number(value ?? 0).toFixed(2)} ₪`,
                                name,
                            ]}
                        />
                        {/* <Legend wrapperStyle={{paddingTop: 12}} iconType="circle" iconSize={10} /> */}
                        {groups.map((group, index) => (
                            <Bar
                                key={group}
                                dataKey={group}
                                stackId="cost"
                                fill={palette[index % palette.length]}
                                radius={[4, 4, 0, 0]}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        )
    }

    return (
        <Card sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <CardContent sx={{display: 'flex', flexDirection: 'column', gap: 2, p: 3}}>
                <Stack spacing={0.5}>
                    <Typography variant="h6" fontWeight={600} color="text.primary">
                        {t('dashboard.invoices_cost_title', 'Cost Projection & Savings')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t(
                            'dashboard.invoices_cost_subtitle',
                            'Monthly invoice totals grouped by active accounts or consumers.'
                        )}
                    </Typography>
                </Stack>

                {barContent}
            </CardContent>
        </Card>
    )
}

export default InvoicesCostChart
